import type { InfiniteData, QueryClient } from '@tanstack/react-query';
import { api } from '@/services/api';
import { clearActiveCountEndpointUnavailable } from '@/lib/activeCountAvailability';
import { clearArchivedCountEndpointUnavailable } from '@/lib/archivedCountAvailability';
import {
  COMPANY_APPLICATIONS_WARM_LIST_SCOPE,
  applicationMatchesServerScope,
  collectApplicationsFromPositionCaches,
  isSparseApplicationListRow,
  preferNewerApplicationRow,
  type CompanyApplicationsListScope,
} from '@/lib/companyApplicationsList';
import { persistRecentSettledApplications } from '@/lib/recentSettledApplications';
import { mergeApplicationsById } from '@/lib/bulkProcessApplications';
import { queryKeys } from '@/lib/queryKeys';
import type { Application } from '@/types';

/** Query meta — false while progressive pages are still streaming into cache. */
export const APPLICATIONS_HYDRATE_COMPLETE_META = 'hydrateComplete';

const hydrateCompleteByKey = new Map<string, boolean>();

function applicationsHydrateKey(companyId: string, archived: boolean): string {
  return `${companyId}:${archived ? 'archived' : 'active'}`;
}

export function isApplicationsHydrateComplete(
  companyId: string,
  archived = false,
): boolean {
  return hydrateCompleteByKey.get(applicationsHydrateKey(companyId, archived)) === true;
}

/** Clear hydrate flags when list caches are wiped (full page reload). */
export function clearApplicationsHydrateComplete(
  companyId: string,
  archived?: boolean,
): void {
  if (archived === undefined) {
    hydrateCompleteByKey.delete(applicationsHydrateKey(companyId, false));
    hydrateCompleteByKey.delete(applicationsHydrateKey(companyId, true));
    return;
  }
  hydrateCompleteByKey.delete(applicationsHydrateKey(companyId, archived));
}

/** Mark hydrate done (e.g. archived API unavailable — empty result is final). */
export function markApplicationsHydrateComplete(
  companyId: string,
  archived = false,
): void {
  setApplicationsHydrateComplete(companyId, archived, true);
}

function setApplicationsHydrateComplete(
  companyId: string,
  archived: boolean,
  complete: boolean,
): void {
  const key = applicationsHydrateKey(companyId, archived);
  if (complete) hydrateCompleteByKey.set(key, true);
  else hydrateCompleteByKey.delete(key);
}

/** Coalesce tab badge refetches during rapid list patches (bulk poll, queue reconcile). */
export const TAB_COUNT_INVALIDATE_DEBOUNCE_MS = 2_000;

const tabCountInvalidateTimers = new Map<string, ReturnType<typeof setTimeout>>();

function invalidateTabCountsNow(queryClient: QueryClient, companyId: string): void {
  void queryClient.invalidateQueries({
    queryKey: queryKeys.applicationsActiveCount(companyId),
  });
  void queryClient.invalidateQueries({
    queryKey: queryKeys.applicationsArchivedCount(companyId),
  });
  void queryClient.invalidateQueries({
    queryKey: ['applications-filtered-count', companyId],
  });
}

function invalidateArchivedTabCountsNow(queryClient: QueryClient, companyId: string): void {
  void queryClient.invalidateQueries({
    queryKey: queryKeys.applicationsArchivedCount(companyId),
  });
  void queryClient.invalidateQueries({
    queryKey: ['applications-filtered-count', companyId],
  });
}

const archivedTabCountInvalidateTimers = new Map<string, ReturnType<typeof setTimeout>>();

function scheduleInvalidateTabCounts(queryClient: QueryClient, companyId: string): void {
  const prev = tabCountInvalidateTimers.get(companyId);
  if (prev !== undefined) clearTimeout(prev);
  tabCountInvalidateTimers.set(
    companyId,
    setTimeout(() => {
      tabCountInvalidateTimers.delete(companyId);
      invalidateTabCountsNow(queryClient, companyId);
    }, TAB_COUNT_INVALIDATE_DEBOUNCE_MS),
  );
}

/** Archive-tab patches must not refetch active-count (optimistic bump is authoritative). */
function scheduleInvalidateArchivedTabCounts(queryClient: QueryClient, companyId: string): void {
  const prev = archivedTabCountInvalidateTimers.get(companyId);
  if (prev !== undefined) clearTimeout(prev);
  archivedTabCountInvalidateTimers.set(
    companyId,
    setTimeout(() => {
      archivedTabCountInvalidateTimers.delete(companyId);
      invalidateArchivedTabCountsNow(queryClient, companyId);
    }, TAB_COUNT_INVALIDATE_DEBOUNCE_MS),
  );
}

/** Flush pending debounced tab-count invalidation (e.g. after archive). */
export function flushInvalidateTabCounts(queryClient: QueryClient, companyId: string): void {
  const prev = tabCountInvalidateTimers.get(companyId);
  if (prev !== undefined) {
    clearTimeout(prev);
    tabCountInvalidateTimers.delete(companyId);
  }
  invalidateTabCountsNow(queryClient, companyId);
}

/** Merge updates into existing rows and append rows that are not in the list yet. */
function upsertApplicationsList(prev: Application[], updates: Application[]): Application[] {
  if (updates.length === 0) return prev;
  const byId = new Map(prev.map((a) => [a.id, a] as const));
  for (const update of updates) {
    const existing = byId.get(update.id);
    if (!existing && isSparseApplicationListRow(update)) continue;
    byId.set(update.id, existing ? preferNewerApplicationRow(existing, update) : update);
  }
  return Array.from(byId.values());
}

export function resolveApplicationPositionId(
  app: Pick<Application, 'positionId'> & { jobPositionId?: string },
): string {
  return app.positionId || app.jobPositionId || '';
}

type CompanyApplicationsPage = {
  items: Application[];
  hasMore: boolean;
  nextCursor?: string;
};

function patchInfiniteListPages(
  queryClient: QueryClient,
  companyId: string,
  updates: Application[],
  archived = false,
): void {
  const dense = updates.filter((a) => !isSparseApplicationListRow(a));
  if (dense.length === 0) return;

  const entries = queryClient.getQueriesData<InfiniteData<CompanyApplicationsPage>>({
    queryKey: ['applications-list', companyId],
  });

  if (entries.length === 0) {
    const scope = { ...COMPANY_APPLICATIONS_WARM_LIST_SCOPE, archived };
    const items = dense.filter((app) => applicationMatchesServerScope(app, scope));
    if (items.length === 0) return;
    queryClient.setQueryData<InfiniteData<CompanyApplicationsPage>>(
      queryKeys.applicationsList(companyId, scope),
      {
        pages: [{ items, hasMore: true }],
        pageParams: [undefined],
      },
    );
    return;
  }

  for (const [key, old] of entries) {
    const scope = key[2] as CompanyApplicationsListScope | undefined;
    if (!scope || scope.archived !== archived || !old?.pages?.length) continue;
    const scopedDense = dense.filter((app) => applicationMatchesServerScope(app, scope));
    if (scopedDense.length === 0) continue;
    queryClient.setQueryData<InfiniteData<CompanyApplicationsPage>>(key, {
      ...old,
      pages: old.pages.map((page) => ({
        ...page,
        items: upsertApplicationsList(page.items, scopedDense),
      })),
    });
  }
}

function stripInfiniteListPagesForScope(
  queryClient: QueryClient,
  companyId: string,
  ids: Set<string>,
  archived: boolean,
): void {
  if (ids.size === 0) return;
  const entries = queryClient.getQueriesData<InfiniteData<CompanyApplicationsPage>>({
    queryKey: ['applications-list', companyId],
  });
  for (const [key, old] of entries) {
    const scopeArchived = key[2] as CompanyApplicationsListScope | undefined;
    if (!old?.pages?.length || !scopeArchived || scopeArchived.archived !== archived) continue;
    queryClient.setQueryData<InfiniteData<CompanyApplicationsPage>>(key, {
      ...old,
      pages: old.pages.map((page) => ({
        ...page,
        items: page.items.filter((a) => !ids.has(a.id)),
      })),
    });
  }
}

function patchPositionCaches(
  queryClient: QueryClient,
  companyId: string,
  apps: Application[],
  archived: boolean,
): void {
  const byPosition = new Map<string, Application[]>();
  for (const app of apps) {
    const positionId = resolveApplicationPositionId(app);
    if (!positionId) continue;
    const list = byPosition.get(positionId) ?? [];
    list.push(app);
    byPosition.set(positionId, list);
  }
  for (const [positionId, rows] of byPosition) {
    queryClient.setQueryData<Application[]>(
      queryKeys.positionApplications(companyId, positionId, archived),
      (prev = []) => upsertApplicationsList(prev, rows),
    );
  }
}

/**
 * Single write path for application list React Query caches (company + per-position).
 * Pages and hooks should prefer this over direct setQueryData on list keys.
 */
export class ApplicationListSync {
  constructor(private readonly queryClient: QueryClient) {}

  /**
   * Legacy company-wide slice — Phase 1: active hot path uses `applications-list` only.
   * Archived queue may still use `['applications', companyId, true]` until archivio migrates.
   */
  getCompanyList(companyId: string, archived = false): Application[] | undefined {
    if (!archived) return undefined;
    return this.queryClient.getQueryData<Application[]>(
      queryKeys.applications(companyId, true),
    );
  }

  getPositionList(
    companyId: string,
    positionId: string,
    archived = false,
  ): Application[] | undefined {
    return this.queryClient.getQueryData<Application[]>(
      queryKeys.positionApplications(companyId, positionId, archived),
    );
  }

  /** Merge rows into company cache and every touched position cache. */
  patchMany(
    companyId: string,
    updates: Application[],
    archived = false,
    options?: { skipTabCountInvalidation?: boolean },
  ): void {
    if (updates.length === 0) return;
    if (archived) {
      this.queryClient.setQueryData<Application[]>(
        queryKeys.applications(companyId, true),
        (prev = []) => upsertApplicationsList(prev, updates),
      );
    }
    patchPositionCaches(this.queryClient, companyId, updates, archived);
    patchInfiniteListPages(this.queryClient, companyId, updates, archived);
    persistRecentSettledApplications(companyId, updates);
    if (options?.skipTabCountInvalidation) return;
    if (archived) {
      scheduleInvalidateArchivedTabCounts(this.queryClient, companyId);
    } else {
      scheduleInvalidateTabCounts(this.queryClient, companyId);
    }
  }

  /** Union legacy company list + all `applications-list` infinite pages (deduped by id). */
  getFlattenedListApplications(companyId: string, archived = false): Application[] {
    const byId = new Map<string, Application>();
    if (archived) {
      for (const app of this.getCompanyList(companyId, true) ?? []) {
        byId.set(app.id, app);
      }
    }
    const entries = this.queryClient.getQueriesData<InfiniteData<CompanyApplicationsPage>>({
      queryKey: ['applications-list', companyId],
    });
    for (const [key, data] of entries) {
      if (!data?.pages?.length) continue;
      const keyScope = key[2] as CompanyApplicationsListScope | undefined;
      if (!keyScope || keyScope.archived !== archived) continue;
      for (const app of data.pages.flatMap((p) => p.items)) {
        const prev = byId.get(app.id);
        byId.set(app.id, prev ? preferNewerApplicationRow(prev, app) : app);
      }
    }
    for (const app of collectApplicationsFromPositionCaches(
      this.queryClient,
      companyId,
      { archived, orderBy: 'updatedAt' },
    )) {
      const prev = byId.get(app.id);
      byId.set(app.id, prev ? preferNewerApplicationRow(prev, app) : app);
    }
    return Array.from(byId.values());
  }

  /** Functional update across company list, infinite list pages, and touched position caches. */
  patchCompanyUpdater(
    companyId: string,
    updater: (prev: Application[]) => Application[],
    archived = false,
  ): void {
    const prev = this.getFlattenedListApplications(companyId, archived);
    const next = updater(prev);
    const nextById = new Map(next.map((a) => [a.id, a]));

    if (archived) {
      this.queryClient.setQueryData<Application[]>(
        queryKeys.applications(companyId, true),
        next,
      );
    }

    const seenInPages = new Set<string>();
    this.queryClient.setQueriesData<InfiniteData<CompanyApplicationsPage>>(
      { queryKey: ['applications-list', companyId] },
      (old) => {
        if (!old?.pages?.length) return old;
        return {
          ...old,
          pages: old.pages.map((page) => ({
            ...page,
            items: page.items.map((a) => {
              seenInPages.add(a.id);
              const patch = nextById.get(a.id);
              return patch ? preferNewerApplicationRow(a, patch) : a;
            }),
          })),
        };
      },
    );

    const additions = next.filter(
      (a) => !seenInPages.has(a.id) && !isSparseApplicationListRow(a),
    );
    if (additions.length > 0) {
      patchInfiniteListPages(this.queryClient, companyId, additions, archived);
    }

    patchPositionCaches(this.queryClient, companyId, next, archived);
    persistRecentSettledApplications(companyId, next);
    scheduleInvalidateTabCounts(this.queryClient, companyId);
  }

  private applicationExistsInCompanyCaches(companyId: string, applicationId: string): boolean {
    return this.getFlattenedListApplications(companyId, false).some((a) => a.id === applicationId);
  }

  /** Refetch tab badge totals immediately (archive flow, upsert fallback). */
  invalidateTabCounts(companyId: string): void {
    flushInvalidateTabCounts(this.queryClient, companyId);
  }

  bumpActiveCount(companyId: string, delta: number): void {
    this.queryClient.setQueryData(
      queryKeys.applicationsActiveCount(companyId),
      (count: number | undefined) => Math.max(0, (count ?? 0) + delta),
    );
    clearActiveCountEndpointUnavailable(companyId);
  }

  /** Fetch one application and upsert into active company + position caches. */
  async upsertOne(
    companyId: string,
    positionId: string,
    applicationId: string,
  ): Promise<void> {
    const application = await api.applications.get(companyId, positionId, applicationId);
    if (!application) {
      await this.invalidateCompany(companyId, false);
      scheduleInvalidateTabCounts(this.queryClient, companyId);
      return;
    }
    const existed = this.applicationExistsInCompanyCaches(companyId, application.id);
    const archived = application.archived === true;
    this.patchMany(companyId, [application], archived);
    if (!archived && !existed) {
      this.bumpActiveCount(companyId, 1);
    }
  }

  /**
   * Position tab showed empty cached [] while company or network may still have rows.
   * Triggers a background refetch when the cached slice is empty.
   */
  ensurePositionListFresh(
    companyId: string,
    positionId: string,
    archived = false,
  ): void {
    const cached = this.getPositionList(companyId, positionId, archived);
    if (cached && cached.length > 0) return;
    void this.queryClient.refetchQueries({
      queryKey: queryKeys.positionApplications(companyId, positionId, archived),
      type: 'active',
    });
  }

  async refetchCompany(companyId: string, archived = false): Promise<void> {
    await this.queryClient.refetchQueries({
      queryKey: queryKeys.applications(companyId, archived),
      type: 'active',
    });
  }

  async refetchAllCompanyScopes(companyId: string): Promise<void> {
    await this.queryClient.refetchQueries({
      queryKey: ['applications-list', companyId],
      type: 'active',
    });
  }

  async invalidateCompany(companyId: string, archived?: boolean): Promise<void> {
    if (archived === undefined) {
      void this.queryClient.invalidateQueries({
        queryKey: ['applications-filtered-count', companyId],
      });
      this.invalidateTabCounts(companyId);
      return;
    }
    await this.queryClient.invalidateQueries({
      queryKey: queryKeys.applications(companyId, archived),
    });
  }

  async invalidatePosition(
    companyId: string,
    positionId: string,
    archived?: boolean,
  ): Promise<void> {
    if (archived === undefined) {
      await this.queryClient.invalidateQueries({
        queryKey: queryKeys.positionApplications(companyId, positionId),
      });
      return;
    }
    await this.queryClient.invalidateQueries({
      queryKey: queryKeys.positionApplications(companyId, positionId, archived),
    });
  }

  /**
   * Background reconcile after archive/unarchive — list rows are patched optimistically.
   * Does not refetch active-count (bulk archive/unarchive already bumped it).
   */
  scheduleArchiveInvalidation(companyId: string, restoredToActive = false): void {
    void this.queryClient.invalidateQueries({
      queryKey: ['applications-filtered-count', companyId],
    });
    void this.queryClient.invalidateQueries({
      queryKey: queryKeys.applicationsArchivedCount(companyId),
    });
    window.setTimeout(() => {
      void this.queryClient.invalidateQueries({
        queryKey: queryKeys.applications(companyId, true),
      });
      if (restoredToActive) {
        void this.queryClient.invalidateQueries({
          queryKey: queryKeys.applications(companyId, false),
        });
      }
      void this.queryClient.refetchQueries({
        predicate: (query) => {
          const key = query.queryKey;
          if (key[0] !== 'applications-list' || key[1] !== companyId) return false;
          if (restoredToActive) return true;
          return (key[2] as CompanyApplicationsListScope | undefined)?.archived === true;
        },
      });
    }, 800);
  }

  bumpArchivedCount(companyId: string, delta: number): void {
    this.queryClient.setQueryData(
      queryKeys.applicationsArchivedCount(companyId),
      (count: number | undefined) => Math.max(0, (count ?? 0) + delta),
    );
    clearArchivedCountEndpointUnavailable(companyId);
  }

  /** Drop rows from active company + infinite list caches (archive optimistic UI). */
  stripFromActiveListCaches(companyId: string, ids: Set<string>): void {
    if (ids.size === 0) return;
    const strip = (prev: Application[] | undefined): Application[] | undefined =>
      (prev ? prev.filter((a) => !ids.has(a.id)) : prev);
    const positionEntries = this.queryClient.getQueriesData<Application[]>({
      queryKey: ['applications', companyId],
    });
    for (const [key] of positionEntries) {
      if (key.length !== 4 || key[3] !== false) continue;
      this.queryClient.setQueryData<Application[]>(key, (prev) => strip(prev) ?? []);
    }
    stripInfiniteListPagesForScope(this.queryClient, companyId, ids, false);
  }

  /** Drop rows from archived list caches after restore-to-active. */
  stripFromArchivedListCaches(companyId: string, ids: Set<string>): void {
    if (ids.size === 0) return;
    const strip = (prev: Application[] | undefined): Application[] | undefined =>
      (prev ? prev.filter((a) => !ids.has(a.id)) : prev);
    this.queryClient.setQueryData<Application[]>(
      queryKeys.applications(companyId, true),
      strip,
    );
    const positionEntries = this.queryClient.getQueriesData<Application[]>({
      queryKey: ['applications', companyId],
    });
    for (const [key] of positionEntries) {
      if (key.length !== 4 || key[3] !== true) continue;
      this.queryClient.setQueryData<Application[]>(key, (prev) => strip(prev) ?? []);
    }
    stripInfiniteListPagesForScope(this.queryClient, companyId, ids, true);
  }
}

export function createApplicationListSync(queryClient: QueryClient): ApplicationListSync {
  return new ApplicationListSync(queryClient);
}
